import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { tollMultiplier, type Leg, type RouteResult, type TollPlazaHit } from "./rota";

/** Destinatário dos feedbacks enviados pelo site. */
const FEEDBACK_RECIPIENT = "matheus.sbk@hotmail.com";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/google_maps";

const placeInput = z.object({
  input: z.string().min(3).max(200),
  sessionToken: z.string().max(80).optional(),
});

const waypoint = z.object({
  placeId: z.string().max(300).optional(),
  address: z.string().min(3).max(300),
});

const routeInput = z.object({
  origin: waypoint,
  destination: waypoint,
  roundTrip: z.boolean(),
  motorcycle: z.boolean(),
  vehicle: z.enum(["carro", "moto", "onibus", "caminhao"]),
  axles: z.number().int().min(2).max(9),
  visitorId: z.string().min(6).max(64).optional(),
});

const feedbackInput = z.object({
  message: z.string().trim().min(5).max(2000),
  contactEmail: z.string().trim().email().max(255).optional().or(z.literal("")),
});

/** Janela de reaproveitamento do cache de rotas (dias). */
const CACHE_DAYS = 30;

/**
 * Teto de cálculos de rota NOVOS (fora do cache) por visitante a cada 24h.
 * Rotas já pesquisadas nos últimos 30 dias saem do cache e não contam:
 * assim o uso legítimo continua livre, mas ninguém consegue drenar a cota
 * da API paga de mapas disparando centenas de trajetos diferentes.
 * O valor comporta viagens com várias paradas (cada trecho conta 1).
 */
const MAX_NEW_ROUTES_PER_DAY = 60;

/**
 * Identifica o visitante pelo identificador estável do navegador (mesmo usado
 * nas estatísticas). Sem ele, cai no IP da borda — e, se nem o IP existir,
 * devolve null para NÃO agrupar desconhecidos num balde compartilhado.
 */
function clientKey(visitorId?: string): string | null {
  if (visitorId) return `v:${visitorId}`;
  const headers = getRequest()?.headers;
  if (!headers) return null;
  const fwd = headers.get("x-forwarded-for")?.split(",")[0]?.trim();
  const ip =
    headers.get("cf-connecting-ip") ?? headers.get("x-real-ip") ?? (fwd || null);
  return ip ? `ip:${ip}` : null;
}

/**
 * Conta e registra cálculos novos do visitante na janela de 24h.
 * Lança erro amigável quando o teto é atingido. Sem identificador
 * confiável do visitante, não limita (melhor que bloquear inocentes).
 */
async function enforceNewRouteBudget(
  supabaseAdmin: { from: (table: string) => any },
  visitorId?: string,
): Promise<void> {
  const key = clientKey(visitorId);
  if (!key) return;
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const { count, error } = await supabaseAdmin
    .from("route_request_log")
    .select("id", { count: "exact", head: true })
    .eq("client_key", key)
    .gte("created_at", since);

  if (!error && (count ?? 0) >= MAX_NEW_ROUTES_PER_DAY) {
    throw new Error(
      "Você atingiu o limite diário de rotas novas. Tente novamente amanhã ou reaproveite uma pesquisa recente.",
    );
  }

  // Registra esta tentativa antes de chamar a API paga: mesmo se o cálculo
  // falhar, a tentativa conta para o limite (evita abuso por repetição).
  await supabaseAdmin.from("route_request_log").insert({ client_key: key });
}

function normalizeKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}


export type Suggestion = { placeId: string; label: string };

function gatewayHeaders() {
  const lovableKey = process.env["LOVABLE_API_KEY"];
  const connectionKey = process.env["GOOGLE_MAPS_API_KEY"];
  if (!lovableKey || !connectionKey) return null;
  return {
    Authorization: `Bearer ${lovableKey}`,
    "X-Connection-Api-Key": connectionKey,
    "Content-Type": "application/json",
  } as Record<string, string>;
}

const MISSING_CONNECTION =
  "O Google Maps ainda não está conectado neste projeto. Conecte o Google Maps Platform para calcular rotas reais.";

export const suggestPlaces = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => placeInput.parse(data))
  .handler(async ({ data }): Promise<{ suggestions: Suggestion[]; error: string | null }> => {
    const headers = gatewayHeaders();
    if (!headers) {
      // Modo de testes gratuito (OpenStreetMap), sem custo por requisição.
      const { freeAutocomplete } = await import("./rota-free.server");
      const suggestions = await freeAutocomplete(data.input);
      return { suggestions, error: suggestions.length ? null : MISSING_CONNECTION };
    }

    const response = await fetch(`${GATEWAY_URL}/places/v1/places:autocomplete`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        input: data.input,
        includedRegionCodes: ["br"],
        languageCode: "pt-BR",
        ...(data.sessionToken ? { sessionToken: data.sessionToken } : {}),
      }),
    });

    if (!response.ok) {
      const body = await response.text();
      console.error(`Places autocomplete failed [${response.status}]: ${body}`);
      return { suggestions: [], error: "Não foi possível buscar sugestões de endereço agora." };
    }

    const json = (await response.json()) as {
      suggestions?: { placePrediction?: { placeId?: string; text?: { text?: string } } }[];
    };

    const suggestions: Suggestion[] = (json.suggestions ?? [])
      .map((s) => ({ placeId: s.placePrediction?.placeId ?? "", label: s.placePrediction?.text?.text ?? "" }))
      .filter((s) => s.placeId && s.label)
      .slice(0, 6);

    return { suggestions, error: null };
  });

type ComputeArgs = {
  headers: Record<string, string>;
  origin: z.infer<typeof waypoint>;
  destination: z.infer<typeof waypoint>;
  motorcycle: boolean;
};

function toWaypoint(w: z.infer<typeof waypoint>) {
  return w.placeId ? { placeId: w.placeId } : { address: w.address };
}

async function computeLeg({ headers, origin, destination, motorcycle }: ComputeArgs): Promise<Leg> {
  const response = await fetch(`${GATEWAY_URL}/routes/directions/v2:computeRoutes`, {
    method: "POST",
    headers: {
      ...headers,
      "X-Goog-FieldMask":
        "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.travelAdvisory.tollInfo",
    },
    body: JSON.stringify({
      origin: { ...toWaypoint(origin) },
      destination: { ...toWaypoint(destination) },
      travelMode: motorcycle ? "TWO_WHEELER" : "DRIVE",
      routingPreference: "TRAFFIC_AWARE",
      languageCode: "pt-BR",
      regionCode: "BR",
      units: "METRIC",
      extraComputations: ["TOLLS"],
      routeModifiers: { vehicleInfo: { emissionType: "GASOLINE" } },
    }),
  });

  if (!response.ok) {
    const body = await response.text();
    console.error(`computeRoutes failed [${response.status}]: ${body}`);
    if (response.status === 403) {
      throw new Error("O Google recusou a requisição (403). Verifique as restrições da chave no Google Cloud.");
    }
    throw new Error("Não foi possível calcular a rota agora. Tente novamente em instantes.");
  }

  const json = (await response.json()) as {
    routes?: {
      distanceMeters?: number;
      duration?: string;
      polyline?: { encodedPolyline?: string };
      travelAdvisory?: {
        tollInfo?: { estimatedPrice?: { currencyCode?: string; units?: string; nanos?: number }[] };
      };
    }[];
  };

  const route = json.routes?.[0];
  if (!route) throw new Error("Nenhuma rota encontrada entre esses pontos.");

  const prices = route.travelAdvisory?.tollInfo?.estimatedPrice ?? [];
  const brl = prices.find((p) => p.currencyCode === "BRL") ?? prices[0];
  const baseTollBrl = brl ? Number(brl.units ?? 0) + (brl.nanos ?? 0) / 1e9 : null;

  return {
    distanceKm: (route.distanceMeters ?? 0) / 1000,
    durationSec: Number(String(route.duration ?? "0s").replace("s", "")),
    baseTollBrl,
    polyline: route.polyline?.encodedPolyline ?? null,
  };
}

export const computeRoute = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => routeInput.parse(data))
  .handler(async ({ data }): Promise<RouteResult> => {
    const originKey = normalizeKey(data.origin.placeId ?? data.origin.address);
    const destinationKey = normalizeKey(data.destination.placeId ?? data.destination.address);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - CACHE_DAYS * 24 * 60 * 60 * 1000).toISOString();

    // 1) Antes de chamar o Google, procuramos no cache uma pesquisa igual (origem,
    // destino, ida e volta, veículo e eixos) feita há menos de 30 dias.
    // O cache só vale se tiver sido calculado com a tabela tarifária vigente:
    // reajuste publicado (ou praça nova na base) derruba o valor guardado.
    const [{ data: cachedRow }, { data: latestTariff }] = await Promise.all([
      supabaseAdmin
        .from("route_cache")
        .select("*")
        .eq("origin_key", originKey)
        .eq("destination_key", destinationKey)
        .eq("round_trip", data.roundTrip)
        .eq("vehicle_type", data.vehicle)
        .eq("axles", data.axles)
        .gte("searched_at", since)
        .order("searched_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabaseAdmin
        .from("toll_tariffs")
        .select("valid_from")
        .is("valid_to", null)
        .order("valid_from", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const currentVersion = latestTariff?.valid_from ?? null;
    const cached =
      cachedRow &&
      cachedRow.outbound_toll_brl !== null &&
      (!currentVersion || cachedRow.tariff_version === currentVersion)
        ? cachedRow
        : null;

    if (cached) {
      // O cache é por veículo/eixos, então o valor gravado já é da categoria.
      const hits = (raw: unknown): TollPlazaHit[] =>
        Array.isArray(raw) ? (raw as TollPlazaHit[]) : [];
      return {
        outbound: {
          distanceKm: Number(cached.outbound_distance_km),
          durationSec: cached.outbound_duration_sec,
          baseTollBrl: null,
          tollBrl: cached.outbound_toll_brl === null ? null : Number(cached.outbound_toll_brl),
          tollPlazas: hits(cached.outbound_toll_plazas),
          polyline: cached.outbound_polyline,
        },
        inbound:
          cached.round_trip && cached.inbound_distance_km !== null
            ? {
                distanceKm: Number(cached.inbound_distance_km),
                durationSec: cached.inbound_duration_sec ?? 0,
                baseTollBrl: null,
                tollBrl: cached.inbound_toll_brl === null ? null : Number(cached.inbound_toll_brl),
                tollPlazas: hits(cached.inbound_toll_plazas),
                polyline: cached.inbound_polyline,
              }
            : null,
        originLabel: cached.origin,
        destinationLabel: cached.destination,
        cached: true,
        searchedAt: cached.searched_at,
        tariffVersion: cached.tariff_version,
      };
    }

    // Rota fora do cache: aplica o teto diário de cálculos novos por
    // visitante antes de acionar a API paga de mapas.
    await enforceNewRouteBudget(supabaseAdmin, data.visitorId);

    const headers = gatewayHeaders();

    let outbound: Leg;
    let inbound: Leg | null;
    let originLabel = data.origin.address;
    let destinationLabel = data.destination.address;
    let originCoord: { lat: number; lng: number } | null = null;
    let destinationCoord: { lat: number; lng: number } | null = null;

    let tariffVersion: string | null = null;

    if (!headers) {
      // Modo sem Google: OpenStreetMap (geocodificação) + OSRM (rota) e a base
      // oficial de praças/tarifas para o pedágio.
      const { freeResolve, freeRoute } = await import("./rota-free.server");
      const [from, to] = await Promise.all([freeResolve(data.origin), freeResolve(data.destination)]);
      originLabel = from.label;
      destinationLabel = to.label;
      originCoord = { lat: from.lat, lng: from.lng };
      destinationCoord = { lat: to.lat, lng: to.lng };

      const [out, back] = await Promise.all([
        freeRoute(from, to),
        data.roundTrip ? freeRoute(to, from) : Promise.resolve(null),
      ]);

      // Cada sentido é conferido na sua própria geometria: praças de sentido
      // único não podem ser espelhadas da ida.
      const { computeRouteToll } = await import("./toll/compute.server");
      const { tollCategory } = await import("./toll/categories");
      const category = tollCategory(data.vehicle, data.axles);
      const [outToll, backToll] = await Promise.all([
        computeRouteToll(out.path, out.refs, category, data.axles),
        back ? computeRouteToll(back.path, back.refs, category, data.axles) : Promise.resolve(null),
      ]);
      tariffVersion = outToll.tariffVersion;

      outbound = {
        distanceKm: out.distanceKm,
        durationSec: out.durationSec,
        baseTollBrl: null,
        polyline: out.polyline,
        tollBrl: outToll.totalBrl,
        tollPlazas: outToll.hits,
      };
      inbound =
        back && backToll
          ? {
              distanceKm: back.distanceKm,
              durationSec: back.durationSec,
              baseTollBrl: null,
              polyline: back.polyline,
              tollBrl: backToll.totalBrl,
              tollPlazas: backToll.hits,
            }
          : null;
    } else {
      outbound = await computeLeg({
        headers,
        origin: data.origin,
        destination: data.destination,
        motorcycle: data.motorcycle,
      });

      inbound = data.roundTrip
        ? await computeLeg({
            headers,
            origin: data.destination,
            destination: data.origin,
            motorcycle: data.motorcycle,
          })
        : null;
    }

    // 2) Gravamos o resultado com a data da pesquisa para reaproveitar por 30 dias.
    const outboundToll = outbound.tollBrl ?? null;
    const inboundToll = inbound?.tollBrl ?? null;
    const multiplier = tollMultiplier(data.vehicle, data.axles);
    const tollTotal =
      outboundToll !== null
        ? outboundToll + (inboundToll ?? 0)
        : outbound.baseTollBrl === null
          ? null
          : (outbound.baseTollBrl + (inbound?.baseTollBrl ?? 0)) * multiplier;

    const searchedAt = new Date().toISOString();
    const { error: cacheError } = await supabaseAdmin.from("route_cache").insert({
      origin: data.origin.address,
      destination: data.destination.address,
      origin_key: originKey,
      destination_key: destinationKey,
      round_trip: data.roundTrip,
      vehicle_type: data.vehicle,
      axles: data.axles,
      outbound_distance_km: outbound.distanceKm,
      outbound_duration_sec: Math.round(outbound.durationSec),
      outbound_toll_brl:
        outboundToll ?? (outbound.baseTollBrl === null ? null : outbound.baseTollBrl * multiplier),
      outbound_toll_plazas: outbound.tollPlazas ?? null,
      outbound_polyline: outbound.polyline,
      inbound_distance_km: inbound?.distanceKm ?? null,
      inbound_duration_sec: inbound ? Math.round(inbound.durationSec) : null,
      inbound_toll_brl:
        inboundToll ??
        (inbound?.baseTollBrl === null || inbound?.baseTollBrl === undefined
          ? null
          : inbound.baseTollBrl * multiplier),
      inbound_toll_plazas: inbound?.tollPlazas ?? null,
      tariff_version: tariffVersion,
      toll_total_brl: tollTotal,
      searched_at: searchedAt,
    });
    if (cacheError) console.error("Falha ao gravar cache de rota:", cacheError.message);

    return {
      outbound,
      inbound,
      originLabel,
      destinationLabel,
      originCoord,
      destinationCoord,
      cached: false,
      searchedAt,
      tariffVersion,
    };
  });

export const sendFeedback = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => feedbackInput.parse(data))
  .handler(async ({ data }): Promise<{ ok: true }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const contactEmail = data.contactEmail?.trim() ? data.contactEmail.trim() : null;

    const { data: row, error } = await supabaseAdmin
      .from("feedback")
      .insert({ message: data.message, contact_email: contactEmail })
      .select("id, created_at")
      .single();

    if (error) {
      console.error("Falha ao salvar feedback:", error.message);
      throw new Error("Não foi possível enviar seu feedback agora. Tente novamente.");
    }

    // O envio do e-mail para FEEDBACK_RECIPIENT é ativado assim que o domínio
    // de e-mail do projeto estiver configurado.
    console.info(`Novo feedback ${row.id} para ${FEEDBACK_RECIPIENT} em ${row.created_at}`);


    return { ok: true };
  });

